import { Component, OnDestroy, OnInit, inject } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete'
import { Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { AuthApiService } from 'src/app/shared/services/api/auth.api.service'
import { UserService } from 'src/app/shared/services/api/user.api.service'
import { AppArea, AppStateService } from 'src/app/shared/services/app-state.service'
import { AuthService } from 'src/app/shared/services/auth.service'
import { LogService } from 'src/app/shared/services/log.service'
import { NotificationService } from 'src/app/shared/services/notification.service'
import { CompanyDto } from 'src/app/types/dto/company.dto'
import { UserDto } from 'src/app/types/dto/user.dto'
import { REDIRECT_URL_KEY } from 'src/app/types/session-state'
import { MatFormField, MatSuffix, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    imports: [MatFormField, MatInput, FormsModule, MatAutocompleteTrigger, ReactiveFormsModule, MatIconButton, MatSuffix, MatIcon, MatAutocomplete, MatOption, MatButton, SpinnerComponent]
})
export class HomeComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  private authApi = inject(AuthApiService);
  private userApi = inject(UserService);
  private appState = inject(AppStateService);
  private logger = inject(LogService);
  private notiService = inject(NotificationService);
  private router = inject(Router);

  currentComp?: CompanyDto
  currentUser?: UserDto
  currentUserEmail?: string
  appVersion?: string

  userCompanies?: CompanyDto[]
  loading = true
  multipleCompanies = false

  companySearchCtrl = new FormControl<string | CompanyDto>('')
  filteredCompanies: CompanyDto[] = []
  private searchSub?: Subscription

  ngOnInit() {
    this.currentComp = this.auth.getCurrentCompany()

    this.appState.setAreaSelected(AppArea.home)

    this.appVersion = this.appState.appVersion

    this.currentUserEmail = this.auth.getUserEmail()
    this.currentUser = this.auth.getCurrentUser()

    this.getCompanies()

    // Set the form control value if a company is already selected
    if (this.currentComp) {
      this.companySearchCtrl.setValue(this.currentComp)
    }
  }

  ngOnDestroy() {
    this.searchSub?.unsubscribe()
  }

  displayCompanyName = (comp: CompanyDto): string => {
    return comp?.companyName ?? ''
  }

  clearCompanySearch() {
    this.companySearchCtrl.setValue('')
    this.currentComp = undefined
    this.filteredCompanies = []
  }

  onCompanySelected(event: MatAutocompleteSelectedEvent) {
    this.currentComp = event.option.value as CompanyDto
    this.compSelected()
  }

  compareComps(comp1: CompanyDto, comp2: CompanyDto) {
    if (!comp1 || !comp2) {
      return false
    } else {
      return comp1.id === comp2.id
    }
  }

  compSelected() {
    if (!this.currentComp) {
      this.logger.log('home', 'compSelected', 'no company in compSelected')
      throw Error('no company in compSelected')
    }

    this.loading = true
    this.auth.setCurrentCompany(this.currentComp)

    // get the user master - only have all user info once a company is selected
    this.userApi.getUser(this.currentComp.userId).subscribe({
      next: (currUser) => {
        if (!this.currentComp) {
          this.logger.log('home', 'compSelected', 'no company in compSelected subscription')
          throw Error('no company in compSelected subscription')
        }
        this.currentUser = currUser
        this.auth.setCurrentUser(this.currentUser)
        this.checkToken(this.currentComp, this.currentUser)
      },
      error: (err) => {
        this.logger.log('home', 'compSelected()', err, 'Error getting user', true)
        this.currentUser = undefined
        this.notiService.handleError(err)
        this.loading = false
      },
    })
  }

  signOut() {
    this.loading = true
    this.auth.signOut()
  }

  private getCompanies() {
    if (!this.currentUserEmail) {
      this.logger.log('home', 'getCompanies()', null, 'no user email', true)
      this.logger.writeLog('no user email in home::getCompanies()', '')
      this.auth.signOut()
      return
    }

    this.authApi.getAuthorisedCompanies(this.currentUserEmail).subscribe({
      next: (companies) => {
        this.userCompanies = companies
        console.info(companies.map(c => c.companyName))
        this.populateCompanies()
      },
      error: (err) => {
        this.logger.log('home', 'populateCompanies()', err, 'Error getting companies', true)
        this.currentUser = undefined
        this.notiService.handleError(err)
        this.loading = false
      },
    })
  }

  private populateCompanies() {
    this.loading = false

    const redirectCompanyId = this.getRedirectCompanyId()

    if (redirectCompanyId && !this.auth.isSignedIn) {
      const match = this.userCompanies?.find((c) => c.id === redirectCompanyId)
      if (match) {
        this.currentComp = match
        this.compSelected()
        return
      } else {
        sessionStorage.removeItem(REDIRECT_URL_KEY)
        this.notiService.showWarning('You do not have access to the company in the link you were trying to access.')
      }
    }

    if (this.userCompanies?.length === 1) {
      this.multipleCompanies = false
      this.currentComp = this.userCompanies[0]

      if (!this.auth.isSignedIn) {
        this.compSelected()
      }
    } else {
      this.multipleCompanies = true
      this.setupCompanyFilter()
    }
  }

  private getRedirectCompanyId(): number | undefined {
    const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY)
    if (!redirectUrl) return undefined
    const match = redirectUrl.match(/^\/(dashboard\/)?(\d+)/)
    return match ? Number(match[2]) : undefined
  }

  private setupCompanyFilter() {
    this.searchSub = this.companySearchCtrl.valueChanges.subscribe((value) => {
      if (typeof value === 'string') {
        this.filteredCompanies =
          value.length >= 2 ? this.filterCompanies(value) : []
      }
    })
  }

  private filterCompanies(search: string): CompanyDto[] {
    const lower = search.toLowerCase()
    return (
      this.userCompanies?.filter((c) =>
        c.companyName.toLowerCase().includes(lower)
      ) ?? []
    )
  }

  private checkToken(currentComp: CompanyDto, currUser: UserDto) {
    this.authApi.checkToken().subscribe({
      next: (res) => {
        if (!res) {
          this.incorrectTokenType()
        } else if (res.correctTokenTypeUsed === false) {
          this.incorrectTokenType(res.message)
        } else {
          // Only proceed to get configurations if token check passes
          this.auth.setCurrentCompany(currentComp) // run again to get the perms

          this.logger.loginLog(currUser.email, true, 'Company ' + currentComp.id.toString())
  
          const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY)
          sessionStorage.removeItem(REDIRECT_URL_KEY)
          this.router.navigateByUrl(redirectUrl ?? 'dashboard')
        }
      },
      error: (err) => {
        console.error('Token check error:', err)
        this.incorrectTokenType()
      },
      complete: () => {
        console.log('Token check completed')
      },
    })
  }

  private incorrectTokenType(message: string | null = null) {
    this.notiService.showError(
      message ?? 'Token check failed. Please sign in again using the correct sign in method.'
    )
    if (this.multipleCompanies) {
      this.currentComp = undefined
      this.loading = false
    } else {
      setTimeout(() => {
        this.signOut()
      }, 5000)
    }
  }
}
